import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, Mail, Code as CodeIcon, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminSocialIconsTile — Admin-only tool that lets the owner download
 * ClearCommunicationApp's official social media icons as SVG, PNG (32/64/128px),
 * AND a copy-paste email-signature HTML snippet pre-wired to the live URLs.
 *
 * Why it lives in admin: keeps the brand-colour SVG paths in one place
 * (mirrors SocialLinks.jsx) so any future palette tweak only happens
 * once, and the icons stay synced with the linktree + footer.
 */
const ICONS = [
  {
    id: 'facebook',
    name: 'Facebook',
    url: 'https://www.facebook.com/share/18dCfoSuwp/',
    bg: '#1877F2',
    fg: '#FFFFFF',
    path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    url: 'https://www.instagram.com/clearcommunicationapp?igsh=MXZjbWZiamQ4bjJpOA==',
    // Instagram's brand uses a gradient; flat fallback color is its mid-tone red-orange.
    bg: '#E1306C',
    fg: '#FFFFFF',
    path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/clearcommunicationapp/',
    bg: '#0A66C2',
    fg: '#FFFFFF',
    path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  },
  {
    id: 'threads',
    name: 'Threads',
    url: 'https://www.threads.com/@clearcommunicationapp',
    bg: '#000000',
    fg: '#FFFFFF',
    path: 'M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.59 12c.025 3.083.717 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.17.408-2.243 1.33-3.023.88-.744 2.084-1.168 3.58-1.263 1.13-.072 2.175.032 3.12.28-.078-.89-.356-1.567-.831-2.005-.59-.545-1.503-.834-2.637-.834h-.047c-.94.01-1.722.27-2.32.776l-1.373-1.49c.93-.775 2.115-1.19 3.517-1.236h.107c1.752 0 3.132.536 3.988 1.55.718.85 1.12 2.015 1.191 3.442.339.163.657.35.95.563 1.068.776 1.823 1.85 2.18 3.103.483 1.694.274 3.835-1.38 5.497-1.795 1.806-4.072 2.63-7.165 2.651zm-.108-7.593c-1.413.088-2.126.747-2.09 1.38.016.285.162.618.556.869.473.303 1.122.43 1.829.393 1.09-.06 1.91-.458 2.438-1.187.36-.497.601-1.122.721-1.862-.75-.2-1.581-.303-2.472-.303-.333 0-.666.016-.982.046v-.001l-.001.665z',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    url: 'https://www.tiktok.com/@clearcommunicationapp',
    bg: '#000000',
    fg: '#FFFFFF',
    path: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
  },
  {
    id: 'youtube',
    name: 'YouTube',
    url: 'https://www.youtube.com/@ClearCommunicationApp',
    bg: '#FF0000',
    fg: '#FFFFFF',
    path: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
  },
];

/** Build an SVG document string for a single icon, with a colored circle
 * background — best for email signatures so the icon stays legible on
 * both dark and light inboxes. */
const buildSvgString = (icon, size = 64) => {
  const r = size / 2;
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="${icon.name}">
  <circle cx="${r}" cy="${r}" r="${r}" fill="${icon.bg}"/>
  <g transform="translate(${size * 0.2} ${size * 0.2}) scale(${size * 0.6 / 24})">
    <path d="${icon.path}" fill="${icon.fg}"/>
  </g>
</svg>`;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Render the SVG to an offscreen canvas and return a PNG blob. */
const svgToPng = async (svgString, size) => {
  return new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, size, size);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        blob ? resolve(blob) : reject(new Error('toBlob failed'));
      }, 'image/png');
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
};

const AdminSocialIconsTile = () => {
  const [size, setSize] = useState(64);
  const [signatureCopied, setSignatureCopied] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const onDownloadSvg = (icon) => {
    const svg = buildSvgString(icon, size);
    downloadBlob(new Blob([svg], { type: 'image/svg+xml' }), `cca-${icon.id}-${size}.svg`);
  };

  const onDownloadPng = async (icon) => {
    try {
      const blob = await svgToPng(buildSvgString(icon, size), size);
      downloadBlob(blob, `cca-${icon.id}-${size}.png`);
    } catch (e) {
      toast.error(`PNG export failed: ${e.message || e}`);
    }
  };

  const onDownloadAllPngs = async () => {
    setBulkLoading(true);
    try {
      for (const icon of ICONS) {
        // Small delay so the browser doesn't merge downloads
        await new Promise((r) => setTimeout(r, 80));
        const blob = await svgToPng(buildSvgString(icon, size), size);
        downloadBlob(blob, `cca-${icon.id}-${size}.png`);
      }
      toast.success(`Downloaded ${ICONS.length} PNGs at ${size}px`);
    } catch (e) {
      toast.error(`Bulk PNG export failed: ${e.message || e}`);
    } finally {
      setBulkLoading(false);
    }
  };

  /** Self-contained inline-SVG email-signature snippet. Inline SVG
   * works in Gmail / Apple Mail / Outlook 365 / Thunderbird. Outlook
   * desktop (old Word renderer) shows the alt text; we link each icon
   * to a real URL so it remains useful even there. */
  const signatureHtml = (() => {
    const sigSize = 28;
    const rows = ICONS.map((ic) => {
      const svg = buildSvgString(ic, sigSize)
        .replace(/<\?xml[^>]+\?>\s*/i, '')
        .replace(/\n\s*/g, '');
      return `<a href="${ic.url}" target="_blank" rel="noopener" style="display:inline-block;margin-right:6px;text-decoration:none" title="${ic.name}">${svg}</a>`;
    }).join('');
    return `<div style="font-family:Arial,sans-serif;font-size:14px;color:#222;line-height:1.5">
  <strong>ClearCommunicationApp</strong><br>
  <em>Bridging neurodivergent communication</em><br>
  <a href="https://clearcommunicationapp.com" style="color:#00A896;text-decoration:none">clearcommunicationapp.com</a>
  <div style="margin-top:8px">${rows}</div>
</div>`;
  })();

  const onCopySignature = async () => {
    try {
      await navigator.clipboard.writeText(signatureHtml);
      setSignatureCopied(true);
      toast.success('Email signature copied — paste into Gmail / Outlook signature settings');
      setTimeout(() => setSignatureCopied(false), 2500);
    } catch {
      toast.error('Copy failed — long-press the textarea below to copy manually');
    }
  };

  return (
    <Card data-testid="admin-social-icons-tile" className="mt-6">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Mail className="h-4 w-4 text-[#00A896]" />
          Social media icons for email signatures
        </CardTitle>
        <CardDescription className="text-xs">
          Download the official ClearCommunicationApp brand icons (SVG + PNG) or copy the
          ready-made email-signature HTML below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Size selector */}
        <div className="flex items-end gap-2">
          <div className="flex-1 max-w-[140px]">
            <Label htmlFor="icon-size-input" className="text-[11px]">PNG size (px)</Label>
            <Input
              id="icon-size-input"
              type="number"
              min={16}
              max={1024}
              step={8}
              value={size}
              onChange={(e) => setSize(Math.max(16, Math.min(1024, Number(e.target.value) || 64)))}
              data-testid="icon-size-input"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onDownloadAllPngs}
            disabled={bulkLoading}
            data-testid="download-all-pngs-btn"
          >
            <Download className="h-3.5 w-3.5 mr-1" />
            {bulkLoading ? 'Exporting…' : `All as PNG @ ${size}px`}
          </Button>
        </div>

        {/* Per-icon row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="social-icons-grid">
          {ICONS.map((ic) => (
            <div
              key={ic.id}
              className="flex items-center gap-2 rounded border p-2"
              data-testid={`social-icon-row-${ic.id}`}
            >
              <div
                className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: ic.bg }}
                dangerouslySetInnerHTML={{
                  __html: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="${ic.fg}"><path d="${ic.path}"/></svg>`,
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{ic.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">{ic.url}</div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDownloadSvg(ic)}
                data-testid={`download-svg-${ic.id}`}
                title="Download SVG"
              >
                <CodeIcon className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDownloadPng(ic)}
                data-testid={`download-png-${ic.id}`}
                title={`Download PNG @ ${size}px`}
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>

        {/* Email signature snippet */}
        <div className="space-y-2 rounded border bg-muted/20 p-3" data-testid="email-signature-block">
          <div className="flex items-center justify-between">
            <Label className="text-[11px] font-semibold">Ready-to-paste email signature HTML</Label>
            <Button
              size="sm"
              onClick={onCopySignature}
              data-testid="copy-signature-btn"
              className="bg-[#00A896] hover:bg-[#008B7A] text-white"
            >
              {signatureCopied
                ? <><Check className="h-3.5 w-3.5 mr-1" /> Copied</>
                : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy HTML</>}
            </Button>
          </div>
          <textarea
            readOnly
            value={signatureHtml}
            className="w-full h-32 font-mono text-[11px] p-2 rounded border bg-background"
            data-testid="signature-textarea"
            onFocus={(e) => e.target.select()}
          />
          <div
            className="text-xs text-muted-foreground"
            data-testid="signature-preview-label"
          >
            Preview:
          </div>
          <div
            className="rounded border bg-background p-3"
            data-testid="signature-preview"
            dangerouslySetInnerHTML={{ __html: signatureHtml }}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSocialIconsTile;
